export type ConfigFieldType = 'mode_select' | 'select' | 'number' | 'boolean' | 'phone' | 'textarea'

export interface ConfigField {
  key: string
  labelSk: string
  labelEn: string
  type: ConfigFieldType
  options?: { value: string; labelSk: string; labelEn: string }[]
  min?: number
  max?: number
  unit?: string
  defaultValue: string | number | boolean
  commandType?: string  // mapovanie na backend príkaz
}

export interface DeviceTypeDefinition {
  nameSk: string
  nameEn: string
  icon: string
  hasGsm?: boolean
  fields: ConfigField[]
}

const motionFields: ConfigField[] = [
  {
    key: 'mode',
    labelSk: 'Režim stráženia',
    labelEn: 'Guard mode',
    type: 'mode_select',
    defaultValue: 'Absencia pohybu',
    commandType: 'set_mode',
  },
  {
    key: 'phone',
    labelSk: 'SMS: Telefónne číslo',
    labelEn: 'SMS: Phone number',
    type: 'phone',
    defaultValue: '421944123456',
    commandType: 'set_phone',
  },
  {
    key: 'smsText',
    labelSk: 'SMS: Text správy',
    labelEn: 'SMS: Message text',
    type: 'textarea',
    defaultValue: 'POZOR: Senzor nezaznamenal pohyb u seniora!',
    commandType: 'set_text',
  },
  {
    key: 'startH',
    labelSk: 'Sledovať od',
    labelEn: 'Monitor from',
    type: 'number',
    min: 0,
    max: 23,
    unit: 'h',
    defaultValue: 7,
    commandType: 'set_start_h',
  },
  {
    key: 'endH',
    labelSk: 'Sledovať do',
    labelEn: 'Monitor until',
    type: 'number',
    min: 0,
    max: 23,
    unit: 'h',
    defaultValue: 22,
    commandType: 'set_end_h',
  },
  {
    key: 'timeout1',
    labelSk: '1. varovanie po',
    labelEn: '1st warning after',
    type: 'number',
    min: 1,
    max: 1440,
    unit: 'min',
    defaultValue: 60,
    commandType: 'set_timeout_1',
  },
  {
    key: 'timeout2',
    labelSk: '2. urgencia po',
    labelEn: '2nd urgency after',
    type: 'number',
    min: 1,
    max: 1440,
    unit: 'min',
    defaultValue: 120,
    commandType: 'set_timeout_2',
  },
  {
    key: 'timeout3',
    labelSk: '3. finálna urgencia po',
    labelEn: '3rd final urgency after',
    type: 'number',
    min: 1,
    max: 1440,
    unit: 'min',
    defaultValue: 120,
    commandType: 'set_timeout_3',
  },
  {
    key: 'hbPeriod',
    labelSk: 'Automatická SMS periodicita',
    labelEn: 'Auto SMS period',
    type: 'select',
    options: [
      { value: 'Denne', labelSk: 'Denne', labelEn: 'Daily' },
      { value: 'Týždenne', labelSk: 'Týždenne', labelEn: 'Weekly' },
      { value: 'Mesačne', labelSk: 'Mesačne', labelEn: 'Monthly' },
      { value: 'Vypnuté', labelSk: 'Vypnuté', labelEn: 'Off' },
    ],
    defaultValue: 'Denne',
    commandType: 'set_hb_period',
  },
  {
    key: 'hbHour',
    labelSk: 'Automatická SMS hodina',
    labelEn: 'Auto SMS hour',
    type: 'number',
    min: 0,
    max: 23,
    unit: 'h',
    defaultValue: 12,
    commandType: 'set_hb_hour',
  },
  {
    key: 'phone2',
    labelSk: 'SMS: Druhé číslo (voliteľné)',
    labelEn: 'SMS: Second number (optional)',
    type: 'phone',
    defaultValue: '',
    commandType: 'set_phone2',
  },
]

export const deviceTypes: Record<string, DeviceTypeDefinition> = {
  motion_v1: {
    nameSk: 'Pohybový senzor',
    nameEn: 'Motion sensor',
    icon: '🏃',
    fields: motionFields,
  },
  motion_v1_wifi: {
    nameSk: 'Pohybový senzor WiFi',
    nameEn: 'Motion sensor WiFi',
    icon: '📶',
    fields: motionFields,
  },
  motion_v1_gsm: {
    nameSk: 'Pohybový senzor GSM',
    nameEn: 'Motion sensor GSM',
    icon: '🏃',
    hasGsm: true,
    fields: motionFields,
  },
  door_v1: {
    nameSk: 'Senzor dverí',
    nameEn: 'Door sensor',
    icon: '🚪',
    fields: [
      {
        key: 'phone',
        labelSk: 'SMS: Telefónne číslo',
        labelEn: 'SMS: Phone number',
        type: 'phone',
        defaultValue: '421944123456',
        commandType: 'set_phone',
      },
      {
        key: 'alertOnOpen',
        labelSk: 'Alarm pri otvorení',
        labelEn: 'Alert on open',
        type: 'boolean',
        defaultValue: true,
      },
      {
        key: 'alertOnClose',
        labelSk: 'Alarm pri zatvorení',
        labelEn: 'Alert on close',
        type: 'boolean',
        defaultValue: false,
      },
    ],
  },
  door_v1_gsm: {
    nameSk: 'Senzor dverí GSM',
    nameEn: 'Door sensor GSM',
    icon: '🚪',
    hasGsm: true,
    fields: [
      {
        key: 'phone',
        labelSk: 'SMS: Telefónne číslo',
        labelEn: 'SMS: Phone number',
        type: 'phone',
        defaultValue: '421944123456',
        commandType: 'set_phone',
      },
      {
        key: 'alertOnOpen',
        labelSk: 'Alarm pri otvorení',
        labelEn: 'Alert on open',
        type: 'boolean',
        defaultValue: true,
      },
      {
        key: 'alertOnClose',
        labelSk: 'Alarm pri zatvorení',
        labelEn: 'Alert on close',
        type: 'boolean',
        defaultValue: false,
      },
    ],
  },
  alarm_v1: {
    nameSk: 'Alarm / Siréna',
    nameEn: 'Alarm / Siren',
    icon: '🚨',
    fields: [
      {
        key: 'sirenDuration',
        labelSk: 'Dĺžka sirény',
        labelEn: 'Siren duration',
        type: 'number',
        min: 5,
        max: 300,
        unit: 's',
        defaultValue: 30,
      },
      {
        key: 'autoDisarm',
        labelSk: 'Automatické vypnutie',
        labelEn: 'Auto disarm',
        type: 'boolean',
        defaultValue: true,
      },
    ],
  },
}

export const getDeviceType = (type?: string | null) =>
  type ? deviceTypes[type] ?? null : null
